import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, BookOpen, Check, LockKeyhole, Quote } from "lucide-react";
import styles from "@/components/library/NotesVisual.module.css";
import { Button } from "@/components/ax/Button";
import { EmptyAxis } from "@/components/ax/EmptyAxis";
import { Sheet } from "@/components/ax/Sheet";
import { useFlash } from "@/hooks/useFlash";
import { useI18n } from "@/i18n";
import { listDecisions } from "@/lib/decisionStore";
import { books } from "@/lib/libraryData";
import { getCatalog, listHighlights, fetchHighlights, saveHighlightToJournal } from "@/lib/libraryStore";
import { getFlags } from "@/lib/flags";

const JournalSheet = ({ highlight, onClose, onSaved }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const triggerRef = useRef(document.activeElement);

  useEffect(() => {
    listDecisions().then((r) => setList((r.decisions ?? []).filter((d) => d.status !== "committed" && d.status !== "deferred")));
  }, []);

  const save = async () => {
    setBusy(true);
    setFailed(false);
    const res = await saveHighlightToJournal(highlight.id, pick);
    setBusy(false);
    if (res.error) return setFailed(true);
    onSaved();
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={t("notes.journal")} testId="journal-sheet" className={styles.journalSheet} returnFocusRef={triggerRef}>
      <p className="m-0 mb-4 text-sm leading-5 text-ax-text-2" data-testid="journal-hint">{t("journal.hint")}</p>
      {!list && <div aria-busy="true" data-testid="journal-skeleton" className={styles.journalLoading}><div /><div /><div /></div>}
      {list && list.length === 0 && (
        <Button variant="primary" onClick={() => navigate("/keputusan/baru")} data-testid="journal-create-button" className="w-full">
          {t("journal.create")}
        </Button>
      )}
      {list && list.length > 0 && (
        <>
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="p-0 text-sm font-medium leading-5 text-ax-text">{t("journal.pick")}</legend>
            <div className="mt-2 flex flex-col gap-2">
              {list.map((d) => (
                <label key={d.id} className="relative block">
                  <input type="radio" name="journal-decision" checked={pick === d.id} onChange={() => setPick(d.id)} data-testid={`journal-decision-${d.id}`} className="peer sr-only" />
                  <span className={`flex min-h-[48px] cursor-pointer items-center rounded-[8px] border px-3 py-2 text-base leading-6 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ax-focus-ring [overflow-wrap:anywhere] ${pick === d.id ? "tint-chip-active" : "border-ax-border"}`}>
                    {d.title}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {failed && <p role="alert" data-testid="journal-error" className="m-0 mt-3 text-sm leading-5 text-ax-error">{t("err.saveItem")}</p>}
          <Button variant="primary" disabled={!pick || busy} busy={busy} onClick={save} data-testid="journal-save-button" className="mt-6 w-full">
            {t("notes.journal")}
          </Button>
        </>
      )}
      <Button onClick={onClose} data-testid="journal-close-button" className="mt-2 w-full">{t("items.close")}</Button>
    </Sheet>
  );
};

export default function LibraryNotes() {
  const { t, lang } = useI18n();
  const [all, setAll] = useState(() => listHighlights());
  const [loading, setLoading] = useState(true);
  const [catalogBooks, setCatalogBooks] = useState([]);
  const [journal, setJournal] = useState(null);
  const [flash, setFlash] = useFlash(2000);
  const locked = getFlags().locked;

  useEffect(() => {
    let active = true;
    fetchHighlights().then((res) => {
      if (active) {
        if (res) setAll(res);
        setLoading(false);
      }
    });
    getCatalog().then((res) => { if (active && res.books) setCatalogBooks(res.books); });
    return () => { active = false; };
  }, []);

  const knownBookIds = new Set(books.map((b) => b.id));
  const otherBookIds = Array.from(new Set(all.map((h) => h.bookId).filter((id) => id && !knownBookIds.has(id))));
  const dynamicBooks = otherBookIds.map((id) => ({
    id,
    title: { id, en: id },
  }));
  const allBooks = [...books, ...dynamicBooks];

  const groups = allBooks
    .map((b) => ({ book: catalogBooks.find((entry) => entry.id === b.id) ?? b, items: all.filter((h) => h.bookId === b.id).sort((a, c) => (c.createdAt || "").localeCompare(a.createdAt || "")) }))
    .filter((g) => g.items.length);

  return (
    <div data-testid="notes-page" className={styles.page}>
      <Link to="/library" data-testid="notes-back-link" className={styles.back}><ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />{t("book.back")}</Link>
      <header className={styles.heading}>
        <div>
          <p className={styles.eyebrow}>{t("lib.archive")}</p>
          <h1 className={styles.title}>{t("notes.title")}</h1>
          <p className={styles.lede}>{t("notes.collectionLede")}</p>
        </div>
        <span className={styles.count} data-testid="notes-count">{t("notes.count", { n: all.length })}</span>
      </header>
      <p role="status" data-testid="notes-flash" className={styles.flash}>{flash}</p>
      {locked && <p data-testid="notes-locked" className={styles.locked}><LockKeyhole size={20} strokeWidth={1.5} aria-hidden="true" />{t("lib.locked")}</p>}

      {loading && groups.length === 0 && <div aria-busy="true" data-testid="notes-skeleton" className={styles.skeleton}>{[0, 1].map((i) => <div key={i} className={styles.skeletonCard}><div /><div /><div /></div>)}</div>}
      {!loading && groups.length === 0 && (
        <section data-testid="notes-empty" className={styles.empty}>
          <EmptyAxis />
          <p>{t("notes.empty")}</p>
        </section>
      )}

      {groups.map(({ book, items }) => (
        <section key={book.id} className={styles.group} aria-labelledby={`notes-${book.id}`} data-testid={`notes-group-${book.id}`}>
          <div className={styles.groupHeader}>
            <h2 id={`notes-${book.id}`} className={styles.groupTitle} data-testid={`notes-book-title-${book.id}`}>{book.title[lang]}</h2>
            <span className={styles.count}>{t("notes.count", { n: items.length })}</span>
          </div>
          <ul className={styles.cards}>
            {items.map((h) => (
              <li
                key={h.id}
                data-testid={`note-card-${h.id}`}
                style={{ borderLeftColor: h.color === "gold" ? "#C9A227" : "#0D9488" }}
                className={styles.card}
              >
                <p className={styles.cardLabel}><Quote size={14} strokeWidth={1.5} aria-hidden="true" />{t("highlight.title")}</p>
                <blockquote className={styles.quote} data-testid={`note-quote-${h.id}`}>{h.text}</blockquote>
                {h.remark && <div className={styles.remark}><p className={styles.remarkLabel}>{t("notes.remarkLabel")}</p><p className={styles.remarkText} data-testid={`note-remark-${h.id}`}>{h.remark}</p></div>}
                <div className={styles.actions}>
                  {locked ? (
                    <span className="inline-flex min-h-[44px] items-center text-sm text-ax-muted">{t("lib.lockedCard")}</span>
                  ) : (
                    <Link to={`/library/${h.bookId}/bab/${h.chapterId}`} data-testid={`note-open-${h.id}`} className="inline-flex min-h-[44px] items-center text-sm font-medium text-ax-link hover:underline">
                      <BookOpen size={16} strokeWidth={1.5} aria-hidden="true" />
                      {t("notes.open")}
                    </Link>
                  )}
                  {h.itemId ? (
                    <span className={styles.done} data-testid={`note-journal-done-${h.id}`}><Check size={14} strokeWidth={1.5} aria-hidden="true" />{t("journal.done")}</span>
                  ) : (
                    <Button variant="ghost" onClick={() => setJournal(h)} data-testid={`note-journal-${h.id}`} className="min-h-[44px] px-0">
                      <ArrowUpRight size={16} strokeWidth={1.5} aria-hidden="true" />
                      {t("notes.journal")}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {journal && (
        <JournalSheet
          highlight={journal}
          onClose={() => setJournal(null)}
          onSaved={() => {
            setJournal(null);
            fetchHighlights().then((res) => setAll(res || listHighlights()));
            setFlash(t("journal.saved"));
          }}
        />
      )}
    </div>
  );
}
